import { useState } from "react"
import { z } from "zod"

import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from "firebase/auth"
import { auth } from "@/lib/firebase"
import { toast } from "sonner"
import { zodResolver } from "@hookform/resolvers/zod"
import { FormProvider, useForm } from "react-hook-form"
import { LoadingSpinner } from "./loading.spinner"
import { FormField, FormItem, FormLabel, FormControl } from "./ui/form"
import { Input } from "./ui/input"
import { useLoginEmail } from "@/api"

export const APPROVED_EMAILS = [
  "tech@cosine.sh",
  "tech+checklytest@cosine.sh",
  "tech+checkly@cosine.sh",
  "tech+slack@cosine.sh",
]

const emailSchema = z.object({ email: z.string().email("Invalid email address") })
type EmailSchema = z.infer<typeof emailSchema>
const formSchema = z.object({
  email: z.string().email(),
  password: z.string().min(6, "Password must be at least 6 characters"),
})
type FormSchema = z.infer<typeof formSchema>

export function useAuth() {
  const [loading, setLoading] = useState(false)
  const login = useLoginEmail({ onError: (err) => toast.error(err.message) })

  async function authenticate(email: string, password: string) {
    setLoading(true)
    try {
      let cred
      try {
        cred = await createUserWithEmailAndPassword(auth, email, password)
      } catch (err: any) {
        if (err.code === "auth/email-already-in-use") {
          cred = await signInWithEmailAndPassword(auth, email, password)
        } else {
          throw err
        }
      }
      const idToken = await cred.user.getIdToken()
      login.mutate({ idToken, refreshToken: cred.user.refreshToken, providerId: "email" })
    } catch (err: any) {
      switch (err.code) {
        case "auth/invalid-email":
          toast.error("Invalid email address")
          break
        case "auth/invalid-credential":
          toast.error("Incorrect password")
          break
        default:
          toast.error("An error occurred. Please try again.")
      }
    } finally {
      setLoading(false)
    }
  }
  return {
    loading,
    authenticate,
  }
}

export function EmailForm() {
  const { authenticate, loading } = useAuth()
  const [step, setStep] = useState<"email" | "password">("email")
  const [verifiedEmail, setVerifiedEmail] = useState("")

  // Step-1: only email + Verify email
  const emailForm = useForm<EmailSchema>({
    resolver: zodResolver(emailSchema),
    defaultValues: { email: "" },
    mode: "onChange",
  })

  // Step-2: email + password
  const passwordForm = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: "", password: "" },
    mode: "onChange",
  })

  const onEmailSubmit = (data: EmailSchema) => {
    const e = data.email.trim().toLowerCase()
    if (!APPROVED_EMAILS.includes(e)) {
      toast.error("Email not authorized. Contact support or SSO at /login")
      return
    }
    setVerifiedEmail(e)

    passwordForm.reset({ email: e, password: "" })
    setStep("password")
  }

  const onPasswordSubmit = (data: FormSchema) => {
    authenticate(data.email, data.password)
  }

  return (
    <>
      {step === "email" ? (
        <FormProvider {...emailForm}>
          <form onSubmit={emailForm.handleSubmit(onEmailSubmit)} className="space-y-6">
            <FormField
              control={emailForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="name@example.com"
                      {...field}
                      disabled={loading}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-md bg-black py-2 text-white"
            >
              {loading ? <LoadingSpinner size={24} /> : "Continue"}
            </button>
          </form>
        </FormProvider>
      ) : (
        <FormProvider {...passwordForm}>
          <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-6">
            <div>
              <FormLabel>Email</FormLabel>
              <Input value={verifiedEmail} disabled className="bg-gray-100" />
            </div>
            <FormField
              control={passwordForm.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="********" {...field} disabled={loading} />
                  </FormControl>
                </FormItem>
              )}
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-md bg-black py-2 text-white"
            >
              {loading ? <LoadingSpinner size={40} className="w-full" /> : "Login"}
            </button>
          </form>
        </FormProvider>
      )}
    </>
  )
}
